import { useState, type ReactNode } from "react";
import { AppWindow, Download, Maximize2, Network, Share2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import type { ArtifactPreview } from "~/services/artifacts";
import type { ArtifactCardActions } from "./ArtifactCardActions";
import { DownloadDialog } from "./DownloadDialog";
import { useCanDownload } from "./useArtifactActions";

interface ArtifactToolbarProps {
  preview: ArtifactPreview;
  actions: ArtifactCardActions | null;
  // Optional expand-to-modal action — wired by the shell when the
  // renderer supplies an `expandedContent` slot.
  onExpand?: () => void;
  // Optional open-in-the-side-panel action for artifacts that render as an app.
  onOpenAsApp?: () => void;
}

// Action row on the right of every artifact card header:
//   [Lineage]? | [share]? [↓]? [app]? [⤢]?
// Lineage is the one labelled button: it opens the side panel that explains
// the card. The rest are icon-only, with tooltips for labels. Download opens a
// modal that handles destination, format and progress; it is hidden on
// share-link views, whose download endpoint is not token-scoped. The dialog
// mounts only while open: a page of cards would otherwise run one dialog's
// hooks per card for nothing.
export const ArtifactToolbar = ({
  preview,
  actions,
  onExpand,
  onOpenAsApp,
}: ArtifactToolbarProps) => {
  const [downloadOpen, setDownloadOpen] = useState(false);
  const canDownload = useCanDownload();

  return (
    <>
      <ArtifactActionButtons
        actions={actions}
        onDownload={canDownload ? () => setDownloadOpen(true) : undefined}
        onExpand={onExpand}
        onOpenAsApp={onOpenAsApp}
      />
      {downloadOpen && (
        <DownloadDialog
          open
          onClose={() => setDownloadOpen(false)}
          artifactId={preview.artifact_id}
          artifactType={preview.artifact_type}
        />
      )}
    </>
  );
};

interface ArtifactActionButtonsProps {
  actions: ArtifactCardActions | null;
  onDownload?: () => void;
  onExpand?: () => void;
  onOpenAsApp?: () => void;
}

/** The toolbar's buttons alone, for rows that show an artifact without its
 *  card, such as the Artifacts page's list view. */
export const ArtifactActionButtons = ({
  actions,
  onDownload,
  onExpand,
  onOpenAsApp,
}: ArtifactActionButtonsProps) => {
  const lineage = actions?.lineage ?? null;
  const share = actions?.share ?? null;

  return (
    <div className="flex items-center gap-0.5">
      {lineage && (
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={lineage.open}
            aria-pressed={lineage.isOpen}
            className={cn("h-7 gap-1.5 px-2.5 text-[13px]", lineage.isOpen && "bg-accent")}
          >
            <Network className="size-3.5" aria-hidden />
            Lineage
          </Button>
          <div className="mx-1 h-4 w-px bg-border" aria-hidden />
        </>
      )}
      {share && (
        <IconAction
          label={share.isShared ? "Shared to project · click to unshare" : "Share to project"}
          icon={<Share2 className="size-3.5" />}
          onClick={share.toggle}
          disabled={!share.isReady}
          pressed={share.isShared}
          className={cn(
            share.isShared &&
              "bg-violet-50 text-violet-600 hover:bg-violet-100 hover:text-violet-700 dark:bg-violet-950/50 dark:text-violet-300"
          )}
        />
      )}
      {onDownload && (
        <IconAction
          label="Download"
          icon={<Download className="size-3.5" />}
          onClick={onDownload}
        />
      )}
      {onOpenAsApp && (
        <IconAction
          label="Open as app"
          icon={<AppWindow className="size-3.5" />}
          onClick={onOpenAsApp}
        />
      )}
      {onExpand && (
        <IconAction label="Expand" icon={<Maximize2 className="size-3.5" />} onClick={onExpand} />
      )}
    </div>
  );
};

interface IconActionProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  pressed?: boolean;
  className?: string;
}

const IconAction = ({ label, icon, onClick, disabled, pressed, className }: IconActionProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={onClick}
        aria-label={label}
        aria-pressed={pressed}
        className={cn("h-7 w-7 p-0 text-muted-foreground", className)}
      >
        {icon}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);
